import { Link } from "react-router-dom";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";
import { Heart, Shield, Landmark, GraduationCap, Users, Monitor, Store, Sliders, ArrowRight, CheckCircle, Mail, Phone, ExternalLink } from "lucide-react";

const WELFARE_CONTRATTUALE = [
  { nome: "Sanimpresa", url: "https://www.sanimpresa.it/", desc: "Fondo di assistenza sanitaria integrativa per i dipendenti del settore, con copertura di visite, esami e prestazioni sanitarie." },
  { nome: "Fonte", url: "https://www.fondofonte.it/", desc: "Fondo di previdenza complementare che consente ai lavoratori di accumulare una pensione integrativa." },
  { nome: "Quas", url: "https://www.quas.it/", desc: "Fondo sanitario per i dirigenti del terziario, con prestazioni dedicate alla salute e al benessere." },
  { nome: "Quadrifor", url: "https://quadrifor.it/", desc: "Ente bilaterale per la formazione e lo sviluppo delle competenze dei quadri del settore." },
  { nome: "Ebit Lazio", url: "https://www.ebitlazio.it/", desc: "Ente bilaterale regionale che supporta imprese e lavoratori con servizi di welfare, formazione e sostegno al reddito." },
];

const CONVENZIONI_WELFARE = [
  {
    nome: "Edenred",
    desc: "Piattaforma di Flexible Benefits con la più ampia rete di punti vendita convenzionati in Italia: buoni pasto Ticket Restaurant, buoni acquisto e piattaforma welfare digitale. Sconto del 20% riservato agli associati Confcommercio Roma.",
    url: "https://www.confcommercioroma.it/convenzioni/welfare-aziendale-edenred/",
  },
  {
    nome: "Double You",
    desc: "Società di consulenza del Gruppo Zucchetti specializzata nel Welfare Management, con la piattaforma ZWelfare per la gestione dei flexible benefit. Sconto del 20% sul listino riservato agli associati.",
    url: "https://www.confcommercioroma.it/convenzioni/double-you-portale-e-servizi-di-welfare-aziendale/",
  },
];

export default function WelfareAziendalePage() {
  return (
    <main id="main-content">
      {/* Hero */}
      <section className="relative h-[280px] md:h-[320px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=1920&q=60&auto=format" alt="Welfare aziendale" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">Welfare aziendale</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-secondary)] leading-relaxed mb-8">Il welfare rappresenta un'opportunità strategica per migliorare il benessere dei dipendenti e ottenere vantaggi fiscali per le imprese. Attraverso soluzioni digitali e piattaforme intuitive, le aziende possono offrire benefit personalizzati che conciliano vita lavorativa e privata, incentivano la produttività e migliorano il clima aziendale.</p>

          {/* Soluzioni digitali + Benefici */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="p-6 bg-[#EBF5FF] rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <Monitor size={22} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">Soluzioni digitali per il welfare</h2>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                  <span className="w-1.5 h-1.5 bg-[var(--confcom-blue)] rounded-full mt-2 shrink-0" />
                  <span><strong className="text-[var(--confcom-text-primary)]">Portali e app intuitive</strong> per la gestione dei benefit aziendali</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                  <span className="w-1.5 h-1.5 bg-[var(--confcom-blue)] rounded-full mt-2 shrink-0" />
                  <span><strong className="text-[var(--confcom-text-primary)]">Reti convenzionate</strong> con migliaia di punti vendita per servizi e acquisti</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                  <span className="w-1.5 h-1.5 bg-[var(--confcom-blue)] rounded-full mt-2 shrink-0" />
                  <span><strong className="text-[var(--confcom-text-primary)]">Strumenti flessibili</strong> per adattarsi alle diverse esigenze aziendali</span>
                </li>
              </ul>
            </div>
            <div className="p-6 bg-[#EBF5FF] rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <Heart size={22} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">Benefici per aziende e dipendenti</h2>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                  <span className="w-1.5 h-1.5 bg-[var(--confcom-blue)] rounded-full mt-2 shrink-0" />
                  <span><strong className="text-[var(--confcom-text-primary)]">Risparmio fiscale</strong> per l'impresa</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                  <span className="w-1.5 h-1.5 bg-[var(--confcom-blue)] rounded-full mt-2 shrink-0" />
                  <span><strong className="text-[var(--confcom-text-primary)]">Maggiore attrattività e retention</strong> del personale</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                  <span className="w-1.5 h-1.5 bg-[var(--confcom-blue)] rounded-full mt-2 shrink-0" />
                  <span><strong className="text-[var(--confcom-text-primary)]">Miglior equilibrio</strong> tra vita lavorativa e personale</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            Scopri{" "}
            <a href="#convenzioni-welfare" className="text-[var(--confcom-blue)] font-semibold hover:underline">
              le nostre convenzioni con i partner del welfare
            </a>
            . Implementare un piano di welfare efficace significa investire nel capitale umano e nella crescita sostenibile dell'azienda.
          </p>
        </div>
      </section>

      {/* Welfare contrattuale */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">Welfare contrattuale</h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8">Il CCNL Terziario, Distribuzione e Servizi di Confcommercio prevede diverse forme di welfare contrattuale per il benessere di lavoratori e imprese:</p>
          <ul className="space-y-3">
            {WELFARE_CONTRATTUALE.map((w) => (
              <li key={w.nome} className="flex items-start gap-3 text-sm text-[var(--confcom-text-secondary)]">
                <CheckCircle size={16} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  <a
                    href={w.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-[var(--confcom-blue)] hover:underline"
                    data-testid={`welfare-contrattuale-${w.nome.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {w.nome}
                  </a>
                  {" "}— {w.desc}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-[var(--confcom-text-secondary)] mt-8">Questi strumenti contribuiscono a migliorare la qualità del lavoro e il benessere dei dipendenti, offrendo vantaggi concreti sia per i lavoratori che per le aziende.</p>
        </div>
      </section>

      {/* Convenzioni con i partner del welfare */}
      <section className="py-16 bg-white" id="convenzioni-welfare">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Convenzioni</span>
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-4">Le nostre convenzioni con i partner del welfare</h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8 max-w-3xl">Le aziende associate a Confcommercio Roma possono usufruire di condizioni agevolate con i principali partner nazionali del welfare aziendale.</p>
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
            {CONVENZIONI_WELFARE.map((c) => (
              <a
                key={c.nome}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-6 bg-[#EBF5FF] rounded-xl border border-[var(--confcom-border)] hover:shadow-md transition-shadow group"
                data-testid={`convenzione-welfare-${c.nome.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm">
                  <Store size={18} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                </div>
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{c.nome}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-snug mb-4">{c.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--confcom-blue)] group-hover:underline">
                  Scopri la convenzione <ExternalLink size={14} aria-hidden="true" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=60&auto=format" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.88)" }} />
        </div>
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per maggiori informazioni</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mt-3 mb-8">Contattaci</h2>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-left" data-testid="welfare-aziendale-contatti">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-white/80">
                <Mail size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="mailto:sindacale@confcommercioroma.it" className="hover:text-white transition-colors">sindacale@confcommercioroma.it</a>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Phone size={14} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <a href="tel:+390668437221" className="hover:text-white transition-colors">Tel: 06 68437 221-290</a>
              </div>
            </div>
          </div>

          <Link
            to="/chi-siamo/struttura-tecnica#ufficio-lavoro-e-welfare"
            data-testid="welfare-aziendale-scopri-team"
            className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-6 py-3 rounded-md font-semibold hover:bg-white/90 transition-colors text-sm mt-8"
          >
            Scopri il Team <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  );
}
